import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  FilterList,
  School,
  Person,
  TrendingUp,
  Schedule,
  Star,
  Calculate,
  Language,
  Science,
  History,
  Brush,
  MusicNote,
  Book,
  Add,
  CheckCircle,
} from '@mui/icons-material';
import { courseService } from '../services/courseService';
import { courseEnrollmentService } from '../services/courseEnrollmentService';
import { childManagementService, ChildSearchResult } from '../services/childManagementService';
import { authService } from '../services/authService';
import type { Course } from '../types';

interface Child {
  id: number;
  name: string;
  avatar: string;
  email: string;
}

const ParentCourses: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChild, setSelectedChild] = useState<number | 'all'>('all');
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [enrollmentStatus, setEnrollmentStatus] = useState<{ [courseId: number]: { [childId: number]: boolean } }>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get childId from URL query parameter
  const childIdFromUrl = searchParams.get('childId');

  useEffect(() => {
    loadData();
  }, []);

  // Update selected child when childId is in URL
  useEffect(() => {
    if (childIdFromUrl) {
      const childIdNum = parseInt(childIdFromUrl, 10);
      if (!isNaN(childIdNum)) {
        setSelectedChild(childIdNum);
      }
    }
  }, [childIdFromUrl]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load linked children
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        const linkedChildren = await childManagementService.getLinkedChildren(currentUser.id);
        const childrenData: Child[] = linkedChildren.map(child => ({
          id: child.id,
          name: `${child.firstName} ${child.lastName}`,
          avatar: child.firstName.charAt(0).toUpperCase(),
          email: child.email,
        }));
        setChildren(childrenData);

        // Load enrollment status for each child
        const enrollmentMap: { [courseId: number]: { [childId: number]: boolean } } = {};
        for (const child of childrenData) {
          try {
            const enrollments = await courseEnrollmentService.getActiveEnrollments(child.id);
            enrollments.forEach(enrollment => {
              if (!enrollmentMap[enrollment.courseId]) {
                enrollmentMap[enrollment.courseId] = {};
              }
              enrollmentMap[enrollment.courseId][child.id] = true;
            });
          } catch (error) {
            console.error(`Error loading enrollments for child ${child.id}:`, error);
          }
        }
        setEnrollmentStatus(enrollmentMap);
      }

      // Load available courses
      const availableCourses = await courseService.getAvailableCourses();
      setCourses(availableCourses);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load courses. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId: number, childId: number) => {
    try {
      setEnrolling({ ...enrolling, [`${courseId}-${childId}`]: true });
      
      const currentUser = authService.getCurrentUser();
      const enrolledBy = currentUser ? `${currentUser.firstName} ${currentUser.lastName} (${currentUser.email})` : 'Parent';
      
      await courseEnrollmentService.enrollUserInCourse(childId, courseId, enrolledBy);
      
      // Update enrollment status
      setEnrollmentStatus(prev => {
        const newStatus = { ...prev };
        if (!newStatus[courseId]) {
          newStatus[courseId] = {};
        }
        newStatus[courseId][childId] = true;
        return newStatus;
      });

      setSnackbar({
        open: true,
        message: 'Child enrolled successfully!',
        severity: 'success',
      });

      // Refresh parent dashboard data
      window.dispatchEvent(new Event('childEnrolled'));
    } catch (error: any) {
      console.error('Error enrolling child:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to enroll child in course';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setEnrolling({ ...enrolling, [`${courseId}-${childId}`]: false });
    }
  };

  const isChildEnrolled = (courseId: number, childId: number): boolean => {
    return enrollmentStatus[courseId]?.[childId] === true;
  };

  const getSubjectIcon = (subject: string) => {
    const subjectLower = subject.toLowerCase();
    if (subjectLower.includes('math') || subjectLower.includes('secondary school')) {
      return <Calculate color="primary" />;
    } else if (subjectLower.includes('english') || subjectLower.includes('primary school')) {
      return <Language color="secondary" />;
    } else if (subjectLower.includes('science')) {
      return <Science color="success" />;
    } else if (subjectLower.includes('history')) {
      return <History color="info" />;
    } else if (subjectLower.includes('technology') || subjectLower.includes('programming')) {
      return <School color="warning" />;
    } else if (subjectLower.includes('art')) {
      return <Brush color="warning" />;
    } else if (subjectLower.includes('music') || subjectLower.includes('piano')) {
      return <MusicNote color="error" />;
    } else if (subjectLower.includes('chess')) {
      return <TrendingUp color="info" />;
    }
    return <Book />;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'not_started':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // If a specific child is selected, show courses they're NOT enrolled in (for enrollment)
    // If 'all' is selected, show all courses
    let matchesChild = true;
    if (selectedChild !== 'all') {
      // For enrollment page, show courses the child is NOT enrolled in
      matchesChild = !isChildEnrolled(course.id, selectedChild);
    }
    
    // Map course type to subject for filtering
    const courseSubject = course.courseType?.replace('_', ' ') || '';
    const matchesSubject = selectedSubject === 'all' || courseSubject === selectedSubject;
    
    const matchesDifficulty = selectedDifficulty === 'all' || 
                             course.difficultyLevel?.toLowerCase() === selectedDifficulty.toLowerCase();

    const matchesRegistration =
      selectedChild === 'all' ||
      String(course.registrationMethod || 'CODE').toUpperCase() === 'OPEN';

    return matchesSearch && matchesChild && matchesSubject && matchesDifficulty && matchesRegistration;
  });

  const subjects = Array.from(new Set(courses.map(course => course.courseType?.replace('_', ' ') || 'Other')));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/parent-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Typography color="text.primary">Courses</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/parent-dashboard')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {selectedChild !== 'all' 
              ? `Register ${children.find(c => c.id === selectedChild)?.name || 'Child'} for a Course`
              : 'Course Overview'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {selectedChild !== 'all'
              ? 'Select a course to enroll your child'
              : 'Monitor your children\'s course progress and performance'}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Child</InputLabel>
              <Select
                value={selectedChild}
                label="Child"
                onChange={(e) => setSelectedChild(e.target.value as number | 'all')}
              >
                <MenuItem value="all">All Children</MenuItem>
                {children.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    {child.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubject}
                label="Subject"
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                <MenuItem value="all">All Subjects</MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {selectedChild === 'all' && (
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                fullWidth
                onClick={() => {
                  if (children.length === 1) {
                    setSelectedChild(children[0].id);
                  } else {
                    // If multiple children, show dialog to select
                    // For now, just select first child
                    setSelectedChild(children[0]?.id || 'all');
                  }
                }}
                sx={{ height: '56px' }}
              >
                Register Course
              </Button>
            </Grid>
          )}
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={selectedDifficulty}
                label="Difficulty"
                onChange={(e) => setSelectedDifficulty(e.target.value)}
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={selectedChild === 'all' ? 2 : 2}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              fullWidth
              onClick={() => {
                setSearchTerm('');
                setSelectedChild(childIdFromUrl ? parseInt(childIdFromUrl, 10) : 'all');
                setSelectedSubject('all');
                setSelectedDifficulty('all');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Header for enrollment */}
      {selectedChild !== 'all' && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Register {children.find(c => c.id === selectedChild)?.name || 'Child'} for a Course
            </Typography>
            <Typography variant="body2">
              Select a course below to enroll your child. Only available courses that your child is not already enrolled in are shown.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Available Courses for Enrollment */}
      {selectedChild !== 'all' ? (
        <Grid container spacing={3}>
          {filteredCourses.map((course) => (
            <Grid item xs={12} md={6} lg={4} key={course.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {getSubjectIcon(course.courseType?.replace('_', ' ') || 'Other')}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {course.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {course.courseType?.replace('_', ' ')} • {course.difficultyLevel || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {course.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={course.difficultyLevel || 'N/A'} 
                      color={getDifficultyColor(course.difficultyLevel?.toLowerCase() || 'medium') as any}
                      size="small" 
                    />
                    {course.durationWeeks && (
                      <Chip label={`${course.durationWeeks} weeks`} variant="outlined" size="small" />
                    )}
                    {course.maxStudents && (
                      <Chip label={`Max ${course.maxStudents} students`} variant="outlined" size="small" />
                    )}
                  </Box>
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    startIcon={enrolling[`${course.id}-${selectedChild}`] ? <CircularProgress size={16} /> : <Add />}
                    disabled={enrolling[`${course.id}-${selectedChild}`] || isChildEnrolled(course.id, selectedChild)}
                    onClick={() => handleEnroll(course.id, selectedChild)}
                    sx={{ 
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: 600,
                    }}
                  >
                    {enrolling[`${course.id}-${selectedChild}`] 
                      ? 'Enrolling...' 
                      : isChildEnrolled(course.id, selectedChild)
                      ? 'Already Enrolled'
                      : 'Enroll in Course'}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        /* Show enrolled courses for all children (existing view) */
        children.map((child) => {
          const childEnrollments = Object.keys(enrollmentStatus)
            .map(courseId => {
              const courseIdNum = parseInt(courseId);
              if (enrollmentStatus[courseIdNum] && enrollmentStatus[courseIdNum][child.id]) {
                return courses.find(c => c.id === courseIdNum);
              }
              return null;
            })
            .filter((course): course is Course => course != null);
        
          if (childEnrollments.length === 0) return null;
        
        return (
          <Card key={child.id} sx={{ mb: 4 }}>
            <CardContent>
              {/* Child Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    {child.avatar}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {child.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {childEnrollments.length} Active Courses
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => navigate(`/parent-child-dashboard/${child.id}`)}
                >
                  View Full Dashboard
                </Button>
              </Box>

              {/* Course Grid for this Child */}
              <Grid container spacing={3}>
                {childEnrollments.map((course) => (
                  <Grid item xs={12} md={6} lg={4} key={course.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          {getSubjectIcon(course.courseType?.replace('_', ' ') || 'Other')}
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {course.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {course.courseType?.replace('_', ' ')} • {course.difficultyLevel || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {course.description}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip 
                            label={course.difficultyLevel || 'N/A'} 
                            color={getDifficultyColor(course.difficultyLevel?.toLowerCase() || 'medium') as any}
                            size="small" 
                          />
                          {course.durationWeeks && (
                            <Chip label={`${course.durationWeeks} weeks`} variant="outlined" size="small" />
                          )}
                        </Box>
                      </CardContent>

                      <Box sx={{ p: 2, pt: 0 }}>
                        <Button 
                          variant="contained" 
                          fullWidth
                          onClick={() => navigate(`/parent-course-details/${course.id}`)}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        );
      }))}

      {/* No Data Message */}
      {selectedChild !== 'all' && filteredCourses.length === 0 && (
        <Alert severity="info" sx={{ textAlign: 'center' }}>
          {children.find(c => c.id === selectedChild)?.name || 'Child'} is already enrolled in all available courses, or no courses match your filters.
        </Alert>
      )}

      {selectedChild === 'all' && children.every(child => {
        const childEnrollments = Object.keys(enrollmentStatus)
          .map(courseId => {
            const courseIdNum = parseInt(courseId);
            if (enrollmentStatus[courseIdNum] && enrollmentStatus[courseIdNum][child.id]) {
              return courses.find(c => c.id === courseIdNum);
            }
            return null;
          })
          .filter((course): course is Course => course != null);
        return childEnrollments.length === 0;
      }) && (
        <Alert severity="info" sx={{ textAlign: 'center' }}>
          No enrolled courses found. Select a child to register them for a course.
        </Alert>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 10000 : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ParentCourses;
